import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useEffect, useState } from 'react'
import { DateTime } from 'luxon'

export type ConnectivityContextType = {
    online: boolean
    offline: boolean
    showOnlineInfo: boolean
    showOfflineWarning: boolean
}

const ConnectivityContext = createContext<ConnectivityContextType>({
    online: true,
    offline: false,
    showOnlineInfo: false,
    showOfflineWarning: false,
})

export function ConnectivityProvider(props: PropsWithChildren): ReactElement {
    const [online, setIsOnline] = useState<boolean>(true)
    const [showOnlineInfo, setShowOnlineInfo] = useState<boolean>(false)
    const [showOfflineWarning, setShowOfflineWarning] = useState<boolean>(false)

    useEffect(() => {
        const handleOffline = () => {
            console.debug(DateTime.now().toUTC().toISO(), 'ConnectivityProvider: window/tab went offline. Showing warning.')
            setIsOnline(false)
            setShowOfflineWarning(true)
        }

        const handleOnline = () => {
            console.debug(DateTime.now().toUTC().toISO(), 'ConnectivityProvider: window/tab went online. Showing info.')
            setIsOnline(true)
            setShowOnlineInfo(true)
            setShowOfflineWarning(false)
            setTimeout(() => {
                console.debug(DateTime.now().toUTC().toISO(), 'ConnectivityProvider: Hiding info.')
                setShowOnlineInfo(false)
            }, 5000)
        }

        window.addEventListener('offline', handleOffline)
        window.addEventListener('online', handleOnline)

        return () => {
            window.removeEventListener('offline', handleOffline)
            window.removeEventListener('online', handleOnline)
        }
    }, [])

    return (
        <ConnectivityContext.Provider
            value={{
                online: online,
                offline: !online,
                showOnlineInfo: showOnlineInfo,
                showOfflineWarning: showOfflineWarning,
            }}
        >
            {props.children}
        </ConnectivityContext.Provider>
    )
}

export default ConnectivityContext
